import React, { useEffect, useState } from "react";
import { View, Text, TouchableOpacity, Image } from "react-native";
import { AppFontStyle } from "../../styles/AppFontStyle";
import { CreateResponsiveStyle, DEVICE_SIZES } from "rn-responsive-styles";
import { AppColors } from "../../utility/AppColors";
import { AppImages } from "../../utility/AppImages";
import { formatTimestamp } from "../formatTimestamp";
import withErrorBoundary from "../../utility/auth/withErrorBoundary";
import { ChatData, useChat } from "../../context/ChatContext";
import { DeleteChat } from "../../services/chatAPI";
import { toast } from "react-toastify";
import { ShowLoader } from "../../common/ShowLoader";
import Avatar from "../choose-avatar-components/Avatar";
import App from "../../../App";

interface ChatUserMembersSectionProps {
  item: ChatData;
  isSelected: boolean;
  onPress: () => void;
}

const ChatUserMembersSection: React.FC<ChatUserMembersSectionProps> = ({
  item,
  isSelected,
  onPress,
}) => {
  const { userData, setChats, userDataRef, currentChatRef, setCurrentChat } =
    useChat();
  const [unreadMessageCount, setUnreadMessageCount] = useState(
    item.unreadCount || 0
  );
  const styles = useStyles();
  const isRead = item.lastMessageRead;
  const [showDeleteButton, setShowDeleteButton] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [currentChatId, setCurrentChatId] = useState<string | null>(null);
  useEffect(() => {
    setCurrentChatId(currentChatRef.current?.conversationId || null);
  }, [currentChatRef.current]);
  const handleDeleteChat = async () => {
    try {
      setShowLoader(true);
      const response = await DeleteChat(
        userDataRef.current.accessToken,
        item.conversationId
      );
      if (response.statusCode === 200) {
        setShowLoader(false);
        setChats((prevChats) =>
          prevChats.filter(
            (chat) => chat.conversationId !== item.conversationId
          )
        );
        if (currentChatRef.current?.conversationId === item.conversationId) {
          setCurrentChat(null);
        }
        toast.success("Chat deleted successfully");
      }
    } catch (error) {
      console.error("Error deleting chat:", error);
    }
  };

  const toggleDeleteButton = () => {
    setShowDeleteButton((prev) => !prev);
  };

  useEffect(() => {
    if (item.conversationId !== currentChatRef.current?.conversationId) {
      setUnreadMessageCount(item.unreadCount);
    }
  }, [item]);
  useEffect(() => {
    if (item.conversationId === currentChatRef.current?.conversationId) {
      setUnreadMessageCount(0);
      setChats((prevChats) =>
        prevChats.map((chat) =>
          chat.conversationId === item.conversationId
            ? { ...chat, unreadCount: 0 }
            : chat
        )
      );
    }
  }, [currentChatId]);
  return (
    <>
      <TouchableOpacity
        onPress={onPress}
        style={[
          styles.itemView,
          {
            backgroundColor: isSelected
              ? AppColors.sidebarBackground_111827
              : AppColors.sidebarBorder_374151,
            borderLeftWidth: isSelected ? 4 : 0,
            borderLeftColor: isSelected
              ? AppColors.appViolentColor_6D08C3
              : "transparent",
            flex: 1,
            gap: 10,
          },
        ]}
      >
        <View
          style={{
            backgroundColor: AppColors.appViolentColor_6D08C3,
            borderRadius: 100,
            alignSelf: "center",
          }}
        >
          <Avatar variant="chatAvatar" src={item.profilePicture} />
        </View>

        {unreadMessageCount > 0 && (
          <View
            style={{
              position: "absolute",
              right: -5,
              top: 0,
              backgroundColor: AppColors.white,
              borderRadius: 12,
              height: 19,
              width: 18,
              justifyContent: "center",
              alignItems: "center",
              zIndex: 10,
              borderWidth: 1,
              borderColor: AppColors.black,
            }}
          >
            <Text
              style={{
                color: AppColors.red_EA3737,
                fontSize: 12,
                fontWeight: "bold",
              }}
            >
              {unreadMessageCount}
            </Text>
          </View>
        )}
        <View style={styles.textContainer}>
          <Text style={[AppFontStyle.REGULAR_ANYBODY_12]} numberOfLines={2}>
            {item.members
              .filter((member) => member !== userData?.username)
              .join(", ")}
          </Text>
          <View style={styles.messageContainer}>
            {item.lastMessageUser === userData?.username && (
              <Image
                source={isRead ? AppImages.icnGreenTick : AppImages.icTick}
                style={styles.icon}
                resizeMode="contain"
              />
            )}
            <Text
              style={[AppFontStyle.REGULAR_ANYBODY_12, styles.message]}
              numberOfLines={1}
            >
              {item.lastMessage
                ? `${
                    item.lastMessageUser === userData?.username
                      ? "You"
                      : item.lastMessageUser
                  } : ${item.lastMessage}`
                : "Start Chat"}
            </Text>
          </View>
        </View>
        <Text style={[AppFontStyle.REGULAR_ANYBODY_12, styles.timestamp]}>
          {formatTimestamp(item.lastMessageTimestamp)}
        </Text>
        {isSelected &&
          item.createdBy === userDataRef.current.username &&
          item.type !== "event" && (
            <TouchableOpacity
              style={{ position: "absolute", right: 37, top: 5 }}
              onPress={toggleDeleteButton}
            >
              <Image
                source={AppImages.icRoundBottom}
                style={{ resizeMode: "contain", width: 19, height: 19 }}
              />
            </TouchableOpacity>
          )}
        {isSelected &&
          item.createdBy === userDataRef.current.username &&
          item.type !== "event" &&
          showDeleteButton && (
            <TouchableOpacity
              style={styles.deleteButton}
              onPress={handleDeleteChat}
            >
              <Text style={AppFontStyle.BOLD_ANYBODY_12}>Delete</Text>
            </TouchableOpacity>
          )}
      </TouchableOpacity>
      {<ShowLoader showIndicater={showLoader} isBackGround={true} />}
    </>
  );
};

const useStyles = CreateResponsiveStyle(
  {
    itemView: {
      borderWidth: 1.9,
      borderColor: AppColors.grayColor_282828,
      borderRadius: 10,
      flexDirection: "row",
      marginVertical: 4,
      marginHorizontal: 10,
      paddingHorizontal: 16,
      paddingVertical: 12,
    },

    textContainer: {
      paddingVertical: 10,
      flex: 1,
    },
    text: {
      paddingHorizontal: 5,
      paddingBottom: 6,
    },
    messageContainer: {
      flexDirection: "row",
    },
    icon: {
      marginTop: 5,
      marginRight: 3,
      height: 16,
      width: 16,
      alignSelf: "center",
    },
    message: {
      opacity: 0.5,
      paddingTop: 5,
      paddingRight: 3,
      alignSelf: "center",
      minWidth: 65,
    },
    timestamp: {
      alignSelf: "center",
      paddingHorizontal: 8,
      opacity: 0.8,
    },
    deleteButton: {
      position: "absolute",
      right: 10,
      top: 30,
      backgroundColor: AppColors.appBlackColor_1e1e1e,
      paddingHorizontal: 16,
      paddingVertical: 7,
      borderColor: AppColors.white,
      borderWidth: 2,
    },
  },
  {
    [DEVICE_SIZES.SM]: {
      itemView: {
        paddingHorizontal: 2,
        paddingVertical: 0,
      },
    },
    [DEVICE_SIZES.LG]: {
      itemView: {
        paddingHorizontal: 2,
        paddingVertical: 0,
      },
    },
    [DEVICE_SIZES.MD]: {
      itemView: {
        paddingHorizontal: 2,
        paddingVertical: 0,
      },
    },
  }
);

export default withErrorBoundary(ChatUserMembersSection);
